// pages/Products/ProductForm.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useForm } from 'react-hook-form';
import { FiSave, FiX, FiUpload } from 'react-icons/fi';
import { productService } from '../../services/productService';
import { categoryService } from '../../services/categoryService';
import ImageUploader from '../../components/Common/ImageUploader';
import LoadingSpinner from '../../components/Common/LoadingSpinner';
import ErrorAlert from '../../components/Common/ErrorAlert';
import { toast } from 'react-hot-toast';
import { getImageUrl } from '../../utils/constants';

const ProductForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [images, setImages] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});

  const { register, handleSubmit, setValue, formState: { errors }, watch } = useForm({
    defaultValues: {
      title: '',
      description: '',
      price: '',
      discount_percent: '0',
      category_id: ''
    }
  });

  const watchedValues = watch();

  const { data: categories, isLoading: categoriesLoading, error: categoriesError } = useQuery(
    'categories',
    categoryService.getAll
  );

  const { data: product, isLoading: productLoading, error: productError } = useQuery(
    ['product', id],
    () => productService.getById(id),
    { enabled: !!id }
  );

  useEffect(() => {
    if (product) {
      setValue('title', product.title || '');
      setValue('description', product.description || '');
      setValue('price', product.price?.toString() || '');
      setValue('discount_percent', product.discount_percent?.toString() || '0');
      setValue('category_id', product.category_id?.toString() || '');
      setExistingImages(product.images || []);
    }
  }, [product, setValue]);

  const createMutation = useMutation(
    (formData) => productService.create(formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
        toast.success('បង្កើតផលិតផលដោយជោគជ័យ');
        navigate('/admin/products');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'មិនអាចបង្កើតផលិតផលបានទេ');
      },
    }
  );

  const updateMutation = useMutation(
    ({ id, formData }) => productService.update(id, formData),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('products');
        toast.success('កែប្រែផលិតផលដោយជោគជ័យ');
        navigate('/admin/products');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'មិនអាចកែប្រែផលិតផលបានទេ');
      },
    }
  );

  const deleteImageMutation = useMutation(
    ({ productId, imageId }) => productService.deleteImage(productId, imageId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['product', id]);
        toast.success('លុបរូបភាពដោយជោគជ័យ');
      },
      onError: (error) => {
        toast.error('មិនអាចលុបរូបភាពបានទេ');
      },
    }
  );

  const validateForm = (data) => {
    const errors = {};
    if (!data.title || data.title.trim() === '') errors.title = 'សូមបញ្ចូលឈ្មោះផលិតផល';
    if (!data.description || data.description.trim() === '') errors.description = 'សូមបញ្ចូលព័ត៌មានលម្អិត';
    if (!data.price || isNaN(parseFloat(data.price)) || parseFloat(data.price) <= 0) errors.price = 'សូមបញ្ចូលតម្លៃត្រឹមត្រូវ';
    if (!data.category_id) errors.category_id = 'សូមជ្រើសរើសប្រភេទ';
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const onSubmit = async (data) => {
    if (!validateForm(data)) {
      toast.error('សូមបំពេញព័ត៌មានដែលត្រូវការ');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    
    formData.append('title', data.title.trim());
    formData.append('description', data.description);
    formData.append('price', parseFloat(data.price).toString());
    formData.append('discount_percent', parseFloat(data.discount_percent || 0).toString());
    formData.append('category_id', parseInt(data.category_id).toString());

    images.forEach((image) => {
      if (image instanceof File) {
        formData.append('images', image);
      }
    });

    try {
      if (id) {
        await updateMutation.mutateAsync({ id, formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
    } finally {
      setUploading(false);
    }
  };

  const handleImageUpload = (newImages) => {
    setImages([...images, ...newImages]);
  };

  const handleImageRemove = (imageToRemove) => {
    if (imageToRemove.id) {
      deleteImageMutation.mutate({
        productId: id,
        imageId: imageToRemove.id
      });
      setExistingImages(existingImages.filter(img => img.id !== imageToRemove.id));
    } else {
      setImages(images.filter(img => img !== imageToRemove));
    }
  };

  if (id && productLoading) return <LoadingSpinner />;
  if (categoriesLoading) return <LoadingSpinner />;
  if (productError) return <ErrorAlert message="មិនអាចផ្ទុកផលិតផលបានទេ" />;
  if (categoriesError) return <ErrorAlert message="មិនអាចផ្ទុកប្រភេទបានទេ" />;

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
        {id ? 'កែប្រែផលិតផល' : 'បន្ថែមផលិតផលថ្មី'}
      </h1>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div className="bg-white border border-gray-200 p-5">
          <h2 className="text-lg font-semibold mb-4">ព័ត៌មានមូលដ្ឋាន</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ឈ្មោះផលិតផល *</label>
              <input
                {...register('title')}
                className="w-full px-4 py-2 border border-gray-200 bg-gray-50 focus:outline-none focus:border-red-600"
                placeholder="បញ្ចូលឈ្មោះផលិតផល"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ប្រភេទ *</label>
              <select
                {...register('category_id')}
                className="w-full px-4 py-2 border border-gray-200 bg-gray-50 focus:outline-none focus:border-red-600"
              >
                <option value="">ជ្រើសរើសប្រភេទ</option>
                {categories?.map(category => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">ព័ត៌មានលម្អិត *</label>
              <textarea
                {...register('description')}
                rows="8"
                className="w-full px-4 py-2 border border-gray-200 font-mono bg-gray-50 focus:outline-none focus:border-red-600"
                placeholder="បញ្ចូលព័ត៌មានលម្អិត (HTML អាចប្រើបាន)"
              />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5">
          <h2 className="text-lg font-semibold mb-4">តម្លៃ</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">តម្លៃ ($) *</label>
              <input
                type="number"
                step="0.01"
                {...register('price')}
                className="w-full px-4 py-2 border border-gray-200 bg-gray-50 focus:outline-none focus:border-red-600"
                placeholder="0.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">បញ្ចុះតម្លៃ (%)</label>
              <input
                type="number"
                step="0.01"
                {...register('discount_percent')}
                className="w-full px-4 py-2 border border-gray-200 bg-gray-50 focus:outline-none focus:border-red-600"
                placeholder="0"
              />
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 p-5">
          <h2 className="text-lg font-semibold mb-4">រូបភាពផលិតផល</h2>
          
          <ImageUploader
            images={[
              ...existingImages.map(img => ({
                id: img.id,
                url: img.fullUrl || getImageUrl(img.filepath, 'products'),
                filepath: img.filepath
              })),
              ...images
            ]}
            onUpload={handleImageUpload}
            onRemove={handleImageRemove}
            maxFiles={10}
            subfolder="products"
          />
          
          <p className="mt-2 text-sm text-gray-500">
            អាចផ្ទុករូបភាពបានរហូតដល់ 10 សន្លឹក។ ទ្រង់ទ្រាយដែលអាចប្រើបាន៖ JPG, PNG, GIF, WEBP
          </p>
        </div>

        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/admin/products')}
            className="px-4 py-2 border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors flex items-center"
          >
            <FiX className="mr-2" /> បោះបង់
          </button>
          <button
            type="submit"
            disabled={uploading}
            className="bg-red-600 text-white px-4 py-2 flex items-center hover:bg-red-700 transition-colors disabled:bg-gray-400"
          >
            {uploading ? (
              <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>កំពុងរក្សាទុក...</>
            ) : (
              <><FiSave className="mr-2" />{id ? 'កែប្រែផលិតផល' : 'រក្សាទុកផលិតផល'}</>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default ProductForm;